import { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from 'recharts';
import { FaChartBar, FaDownload, FaChevronDown, FaSync } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import api from '../../api';

const PERIODS = [
  { value: 'month',   label: 'This Month' },
  { value: 'quarter', label: 'This Quarter' },
  { value: 'ytd',     label: 'Year to Date' },
];

const COLORS = ['#0c1b4d','#1d4ed8','#06b6d4','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#14b8a6','#f97316'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-lg px-4 py-3 text-sm">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="text-xs">
          {p.name}: <span className="font-bold">{typeof p.value === 'number' ? p.value.toFixed(1) : p.value}</span>
        </p>
      ))}
    </div>
  );
};

function SectionCard({ title, subtitle, children, action }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div>
          <h2 className="font-bold text-[#0c1b4d] text-sm">{title}</h2>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

export default function Reports() {
  const [period, setPeriod]         = useState('month');
  const [byEmployee, setByEmployee] = useState([]);
  const [byProject, setByProject]   = useState([]);
  const [tsSummary, setTsSummary]   = useState([]);
  const [leaveSummary, setLeaveSummary] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [exporting, setExporting]   = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.getReportByEmployee(period),
      api.getReportByProject(period),
      api.getTimesheetSummary(),
      api.getLeaveSummary(),
    ])
      .then(([emp, proj, ts, leave]) => {
        setByEmployee(emp.map(r => ({ ...r, hours: parseFloat(r.hours) })));
        setByProject(proj.map(r => ({ ...r, hours: parseFloat(r.hours) })));
        setTsSummary(ts.map(r => ({
          ...r,
          approved: parseInt(r.approved),
          submitted: parseInt(r.submitted),
          rejected: parseInt(r.rejected),
          draft: parseInt(r.draft),
        })));
        // Aggregate leave days by type for pie chart
        const byType = {};
        leave.forEach(r => {
          byType[r.type] = (byType[r.type] || 0) + parseFloat(r.days);
        });
        setLeaveSummary(Object.entries(byType).map(([name, value]) => ({ name, value })));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const data = await api.getReportExport(period);
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, 'Hours Detail');

      // Employee summary sheet
      const empWs = XLSX.utils.json_to_sheet(
        byEmployee.map(r => ({ Employee: r.name, 'Employee ID': r.employee_id, Hours: r.hours }))
      );
      XLSX.utils.book_append_sheet(wb, empWs, 'By Employee');

      // Project summary sheet
      const projWs = XLSX.utils.json_to_sheet(
        byProject.map(r => ({ Project: r.name, Hours: r.hours }))
      );
      XLSX.utils.book_append_sheet(wb, projWs, 'By Project');

      const periodLabel = PERIODS.find(p => p.value === period)?.label.replace(/\s+/g, '_') || period;
      XLSX.writeFile(wb, `MARVY_Report_${periodLabel}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err) {
      alert('Export failed: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  const totalHours = byEmployee.reduce((s, r) => s + r.hours, 0);
  const activeEmployees = byEmployee.filter(r => r.hours > 0).length;

  return (
    <div className="bg-slate-50 min-h-screen">

      {/* Header */}
      <div className="bg-gradient-to-r from-[#0c1b4d] to-[#1d4ed8] text-white">
        <div className="max-w-7xl mx-auto px-4 py-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FaChartBar size={20} /> Reports &amp; Analytics
            </h1>
            <p className="text-blue-200 text-sm mt-1">Hours, timesheets and leave insights</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Period selector */}
            <div className="relative">
              <select
                value={period}
                onChange={e => setPeriod(e.target.value)}
                className="appearance-none bg-white/10 border border-white/20 text-white rounded-xl px-4 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
              >
                {PERIODS.map(p => <option key={p.value} value={p.value} className="text-gray-800">{p.label}</option>)}
              </select>
              <FaChevronDown size={10} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/70 pointer-events-none" />
            </div>
            <button onClick={load} title="Refresh"
              className="p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-colors">
              <FaSync size={14} className={loading ? 'animate-spin' : ''} />
            </button>
            <button onClick={handleExport} disabled={exporting}
              className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-400 text-white font-semibold rounded-xl text-sm transition-colors disabled:opacity-50">
              <FaDownload size={13} /> {exporting ? 'Exporting…' : 'Export Excel'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">

        {/* KPI strip */}
        {!loading && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Total Approved Hours', value: totalHours.toFixed(1) + 'h', color: 'text-blue-600', bg: 'bg-blue-50' },
              { label: 'Active Employees', value: activeEmployees, color: 'text-emerald-600', bg: 'bg-emerald-50' },
              { label: 'Projects Tracked', value: byProject.length, color: 'text-violet-600', bg: 'bg-violet-50' },
              { label: 'Leave Days (YTD)', value: leaveSummary.reduce((s, r) => s + r.value, 0), color: 'text-amber-600', bg: 'bg-amber-50' },
            ].map((k, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <div className={`text-2xl font-bold ${k.color}`}>{k.value}</div>
                <div className="text-sm text-gray-600 mt-0.5">{k.label}</div>
              </div>
            ))}
          </div>
        )}

        {loading && (
          <div className="text-center py-16 text-gray-400 text-sm">Loading reports…</div>
        )}

        {!loading && (
          <>
            {/* Row 1: Hours by Employee + Hours by Project */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

              <SectionCard
                title="Hours by Employee"
                subtitle={PERIODS.find(p => p.value === period)?.label}
              >
                {byEmployee.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-8">No approved hours in this period.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={byEmployee.slice(0, 10)} layout="vertical" margin={{ left: 16, right: 16 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => v + 'h'} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
                      <Tooltip content={<CustomTooltip />} formatter={(v) => [v.toFixed(1) + 'h', 'Hours']} />
                      <Bar dataKey="hours" fill="#0c1b4d" radius={[0, 4, 4, 0]} name="Hours" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </SectionCard>

              <SectionCard
                title="Hours by Project"
                subtitle={PERIODS.find(p => p.value === period)?.label}
              >
                {byProject.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-8">No approved hours in this period.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={byProject} margin={{ left: 8, right: 16 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-30} textAnchor="end" height={50} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => v + 'h'} />
                      <Tooltip content={<CustomTooltip />} formatter={(v) => [v.toFixed(1) + 'h', 'Hours']} />
                      <Bar dataKey="hours" radius={[4, 4, 0, 0]} name="Hours">
                        {byProject.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </SectionCard>
            </div>

            {/* Row 2: Timesheet Status Trend + Leave by Type */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

              <SectionCard title="Timesheet Status (Last 6 Months)" subtitle="Approved · Submitted · Rejected · Draft">
                {tsSummary.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-8">No timesheet data.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={tsSummary} margin={{ left: 0, right: 16 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Line type="monotone" dataKey="approved"  stroke="#10b981" strokeWidth={2} dot={false} name="Approved" />
                      <Line type="monotone" dataKey="submitted" stroke="#f59e0b" strokeWidth={2} dot={false} name="Submitted" />
                      <Line type="monotone" dataKey="rejected"  stroke="#ef4444" strokeWidth={2} dot={false} name="Rejected" />
                      <Line type="monotone" dataKey="draft"     stroke="#9ca3af" strokeWidth={2} dot={false} name="Draft" />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </SectionCard>

              <SectionCard title="Leave Days by Type (YTD Approved)" subtitle="Approved leave requests only">
                {leaveSummary.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-8">No approved leave this year.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie
                        data={leaveSummary}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={3}
                        dataKey="value"
                        nameKey="name"
                        label={({ name, value }) => `${name}: ${value}d`}
                        labelLine={false}
                      >
                        {leaveSummary.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => [v + ' day(s)', '']} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </SectionCard>
            </div>

            {/* Employee hours table */}
            <SectionCard title="Employee Hours Detail" subtitle={PERIODS.find(p => p.value === period)?.label + ' — approved timesheets'}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500 uppercase tracking-wide border-b border-gray-100">
                      <th className="text-left pb-3 font-semibold">Employee</th>
                      <th className="text-left pb-3 font-semibold">ID</th>
                      <th className="text-right pb-3 font-semibold">Approved Hours</th>
                      <th className="text-right pb-3 font-semibold">% of Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {byEmployee.map((r, i) => (
                      <tr key={i} className="hover:bg-slate-50 transition-colors">
                        <td className="py-2.5 font-medium text-gray-800">{r.name}</td>
                        <td className="py-2.5 text-gray-500 text-xs">{r.employee_id}</td>
                        <td className="py-2.5 text-right font-semibold text-[#0c1b4d]">{r.hours.toFixed(1)}h</td>
                        <td className="py-2.5 text-right text-gray-500">
                          {totalHours > 0 ? ((r.hours / totalHours) * 100).toFixed(1) + '%' : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {byEmployee.length > 0 && (
                    <tfoot>
                      <tr className="border-t border-gray-200">
                        <td className="pt-3 font-bold text-gray-800" colSpan={2}>Total</td>
                        <td className="pt-3 text-right font-bold text-[#0c1b4d]">{totalHours.toFixed(1)}h</td>
                        <td className="pt-3 text-right text-gray-500">100%</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </SectionCard>
          </>
        )}
      </div>
    </div>
  );
}
